// src/components/content_editor/ActivityTrackerEditor.js
import React from 'react';
import { Grid, TextField, Typography, Divider, Box, FormControl, InputLabel, Select, MenuItem, InputAdornment } from '@mui/material';

// 1. Updated Default Config to include goals structure
export const DEFAULT_TRACKER_CONFIG = {
    labels: {
        duration: "Duration",
        duration_unit: "minutes",
        difficulty: "Difficulty",
        difficulty_min: "Very Hard",
        difficulty_max: "Very Easy",
        feeling: "How did you feel?",
        feeling_min: "Very Bad",
        feeling_max: "Very Good",
        journal: "Notes or reflections to self",
        promise: "My goal for tomorrow"
    },
    goals: {
        duration: "",
        difficulty: "",
        feeling: ""
    },
    units: ["hours", "minutes"]
};

const ActivityTrackerEditor = ({ config, onChange }) => {
    const effectiveConfig = {
        ...DEFAULT_TRACKER_CONFIG,
        ...(config || {}),
        labels: {
            ...DEFAULT_TRACKER_CONFIG.labels,
            ...(config?.labels || {})
        },
        goals: {
            ...DEFAULT_TRACKER_CONFIG.goals,
            ...(config?.goals || {})
        }
    };

    const { labels, goals } = effectiveConfig;

    // Helper to update nested state
    const commitChange = (section, field, value) => {
        const newConfig = {
            ...effectiveConfig,
            [section]: {
                ...effectiveConfig[section],
                [field]: value
            }
        };
        onChange(newConfig);
    };

    const handleUnitChange = (e) => {
        commitChange('labels', 'duration_unit', e.target.value);
    };

    return (
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: '#fafafa' }}>
            <Grid container spacing={3}>
                {/* Section 1: Duration */}
                <Grid item xs={12}><Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>1. Duration & Time</Typography></Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Duration Question Label"
                        value={labels.duration || ''}
                        onChange={(e) => commitChange('labels', 'duration', e.target.value)}
                        fullWidth size="small"
                        helperText="e.g., 'Time spent wearing brace'"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Primary Unit</InputLabel>
                        <Select
                            value={labels.duration_unit || 'minutes'}
                            label="Primary Unit"
                            onChange={handleUnitChange}
                        >
                            <MenuItem value="hours">Hours</MenuItem>
                            <MenuItem value="minutes">Minutes</MenuItem>
                            <MenuItem value="reps">Repetitions</MenuItem>
                            <MenuItem value="steps">Steps</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                {/* Section 2: Difficulty */}
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12}><Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>2. Input Slider A (Effort/Difficulty)</Typography></Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Slider Title"
                        value={labels.difficulty || ''}
                        onChange={(e) => commitChange('labels', 'difficulty', e.target.value)}
                        fullWidth size="small"
                        helperText="e.g., 'Discomfort Level'"
                    />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <TextField
                        label="Min Label (Left)"
                        value={labels.difficulty_min || ''}
                        onChange={(e) => commitChange('labels', 'difficulty_min', e.target.value)}
                        fullWidth size="small"
                    />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <TextField
                        label="Max Label (Right)"
                        value={labels.difficulty_max || ''}
                        onChange={(e) => commitChange('labels', 'difficulty_max', e.target.value)}
                        fullWidth size="small"
                    />
                </Grid>

                {/* Section 3: Feeling */}
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12}><Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>3. Input Slider B (Outcome/Feeling)</Typography></Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Slider Title"
                        value={labels.feeling || ''}
                        onChange={(e) => commitChange('labels', 'feeling', e.target.value)}
                        fullWidth size="small"
                        helperText="e.g., 'How did you feel?'"
                    />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <TextField
                        label="Min Label (Left)"
                        value={labels.feeling_min || ''}
                        onChange={(e) => commitChange('labels', 'feeling_min', e.target.value)}
                        fullWidth size="small"
                    />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <TextField
                        label="Max Label (Right)"
                        value={labels.feeling_max || ''}
                        onChange={(e) => commitChange('labels', 'feeling_max', e.target.value)}
                        fullWidth size="small"
                    />
                </Grid>

                {/* NEW Section 5: Goals */}
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12}><Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>5. Patient Goals (Visualized on Charts)</Typography></Grid>
                
                <Grid item xs={12} sm={4}>
                    <TextField
                        label={`Target Duration (${labels.duration_unit})`}
                        value={goals.duration || ''}
                        onChange={(e) => commitChange('goals', 'duration', e.target.value)}
                        fullWidth size="small"
                        type="number"
                        InputProps={{
                            endAdornment: <InputAdornment position="end">{labels.duration_unit === 'hours' ? 'hrs' : 'min'}</InputAdornment>,
                        }}
                        helperText="Set to 0 to hide goal line."
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Target Difficulty (0-10)"
                        value={goals.difficulty || ''}
                        onChange={(e) => commitChange('goals', 'difficulty', e.target.value)}
                        fullWidth size="small"
                        type="number"
                        helperText="Ideal effort/pain level."
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Target Feeling (0-10)"
                        value={goals.feeling || ''}
                        onChange={(e) => commitChange('goals', 'feeling', e.target.value)}
                        fullWidth size="small"
                        type="number"
                        helperText="Ideal outcome level."
                    />
                </Grid>

                {/* Section 4: Text Inputs (Moved to bottom) */}
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12}><Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>4. Optional Text Inputs</Typography></Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Journal/Notes Label"
                        value={labels.journal || ''}
                        onChange={(e) => commitChange('labels', 'journal', e.target.value)}
                        fullWidth size="small"
                        helperText="e.g., 'Notes or Triggers'"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Goal/Promise Label"
                        value={labels.promise || ''}
                        onChange={(e) => commitChange('labels', 'promise', e.target.value)}
                        fullWidth size="small"
                        helperText="e.g., 'My goal for tomorrow'"
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default ActivityTrackerEditor;